import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { SearchNavBar, DataTable } from "../../components/ui";
import { AppStrings, logApiErrorToSentry } from "../../utils";
import { remittanceActions } from "../../state/remittanceSlice";
import { RemitterBanner } from "../../../../assets/Icons";
import KycStatus from "../../components/ui/KycStatus";
import RemitterDetails from "./RemitterDetails";
import {
  AddRemitterModal,
  KycAggrementModal,
  KycSuccessModal,
  SelectQuoteModal,
  UserDocumentsModal,
} from "../../components/modal";
import { remittanceApi } from "../../api/remittanceApi";
import {
  getSearchQuoteDetailsRequest,
  isAvanseMasterVendor,
  isRemitterProfileIncomplete,
} from "../../utils/avanseQuote";

const RemittanceRemitter: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const remitterState = useAppSelector((state) => state.remittance);
  const authState = useAppSelector((state) => state.auth);
  const { calculator, remitter } = useAppSelector((state) => state.remittance);

  const [searchedInput, setSearchedInput] = React.useState<string>("");

  const [pagination, setPagination] = React.useState({
    page: 0,
    size: 10,
    ascending: false,
  });

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    const formData = {
      page: pagination.page,
      size: pagination.size === 0 ? 10 : pagination.size,
      ascending: pagination.ascending,
      filter: searchedInput,
      idNumber: calculator?.amountCalculation?.pan_number ?? "",
    };
    dispatch(remittanceActions.fetchRemitterListRequested(formData));
  }, [pagination, searchedInput]);

  useEffect(() => {
    if (!remitterState.remitter.remitterList.reload) return;
    const formData = {
      page: pagination.page,
      size: pagination.size === 0 ? 10 : pagination.size,
      ascending: pagination.ascending,
      filter: searchedInput,
      idNumber: calculator?.amountCalculation?.pan_number ?? "",
    };
    dispatch(remittanceActions.fetchRemitterListRequested(formData));
    dispatch(remittanceActions.setRemitterListState({ reload: false }));
  }, [remitterState.remitter.remitterList.reload]);

  const onRefreshClick = () => {
    setPagination({
      page: 0,
      size: 10,
      ascending: false,
    });
    setSearchedInput("");
  };

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 2) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  {
    /* Add Remitter */
  }
  const addRemitter = (type: string) => {
    if (type == "withoutotp" || type == "sendkyc") {
      addRemitterSignup(type);
      return;
    }
    if (remitter?.addRemitterInput?.isSentOtp) {
      if (type == "proceed") {
        addRemitterValidateOtp();
      } else if (type == "resendotp") {
        addRemitterSendOtp(type);
      }
    } else {
      addRemitterSendOtp();
    }
  };

  const addRemitterSendOtp = (type = "") => {
    const sendOtpBody = {
      phoneNumber: remitter?.addRemitterInput?.mobile,
      otpType: "mobile",
      actionType: "REMITTER_SIGN_UP",
      userId: authState?.currentUser?.vendor_key,
    };
    dispatch(
      remittanceActions.setAddRemitterResponse(
        type === "resendotp"
          ? { resendOTPLoading: true, error: null }
          : { sendOTPLoading: true, error: null }
      )
    );
    remittanceApi
      .getAddRemitterSendOtp(sendOtpBody)
      .then((response) => {
        dispatch(
          remittanceActions.setAddRemitterResponse({
            sendOTPLoading: false,
            resendOTPLoading: false,
          })
        );
        dispatch(
          remittanceActions.setAddRemitterInput({
            isSentOtp: true,
            resendCount: remitter?.addRemitterInput?.resendCount + 1,
            date: Date.now() + 60000,
          })
        );
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.data ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        dispatch(
          remittanceActions.setAddRemitterResponse({
            sendOTPLoading: false,
            resendOTPLoading: false,
            error: errorMsg,
          })
        );
        toast.error(errorMsg);
        logApiErrorToSentry(err, "addRemitterSendOtp remittanceApi.getAddRemitterSendOtp");
      });
  };

  const addRemitterValidateOtp = () => {
    const validateOtpBody = {
      email_phone: remitter?.addRemitterInput?.mobile,
      otp: remitter?.addRemitterInput?.otp,
      otpType: "mobile",
    };
    dispatch(remittanceActions.setAddRemitterResponse({ validateOTPLoading: true, error: null }));
    remittanceApi
      .getAddRemitterValidateOtp(validateOtpBody)
      .then((response) => {
        if (response?.data?.success) {
          addRemitterSignup();
        } else {
          const errorMsg = response?.data?.message || AppStrings.GENERIC_ERROR;
          toast.error(errorMsg);
          dispatch(remittanceActions.setAddRemitterResponse({ validateOTPLoading: false }));
        }
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        dispatch(
          remittanceActions.setAddRemitterResponse({ validateOTPLoading: false, error: errorMsg })
        );
        toast.error(errorMsg);
        logApiErrorToSentry(err, "addRemitterValidateOtp remittanceApi.getAddRemitterValidateOtp");
      });
  };

  const addRemitterSignup = (type = "") => {
    const signUpBody = {
      email: remitter?.addRemitterInput?.email,
      phoneNumber: remitter?.addRemitterInput?.mobile,
      is_email_verified: true,
      is_phone_verified: true,
    };
    dispatch(
      remittanceActions.setAddRemitterResponse(
        type === "sendkyc"
          ? { sendKycLinkLoading: true, error: null }
          : { signUpLoading: true, error: null, validateOTPLoading: false }
      )
    );
    remittanceApi
      .getAddRemitterSignup(signUpBody)
      .then((response) => {
        dispatch(
          remittanceActions.setAddRemitterInput({
            email: "",
            mobile: "",
            otp: "",
            isSentOtp: false,
          })
        );
        if (type === "sendkyc") {
          if (response?.data?.user_key) {
            remittanceApi
              .getRemitterResendKycLink(response?.data?.user_key)
              .then((response) => {
                toast.success(
                  "KYC Link sent successfully to email and phone number of the remitter"
                );
                dispatch(
                  remittanceActions.setAddRemitterResponse({
                    signUpLoading: false,
                    sendKycLinkLoading: false,
                    validateOTPLoading: false,
                  })
                );
                dispatch(remittanceActions.setModalType(""));
                onRefreshClick();
              })
              .catch((err) => {
                const errorMsg =
                  err?.response?.data?.error?.type ||
                  err?.response?.error?.type ||
                  err?.response?.error ||
                  AppStrings.GENERIC_ERROR;
                dispatch(
                  remittanceActions.setAddRemitterResponse({
                    signUpLoading: false,
                    sendKycLinkLoading: false,
                    error: errorMsg,
                    validateOTPLoading: false,
                  })
                );
                toast.error(errorMsg);
                logApiErrorToSentry(err, "remittanceApi.getRemitterResendKycLink");
              });
          } else {
            toast.error(AppStrings.GENERIC_ERROR);
            dispatch(
              remittanceActions.setAddRemitterResponse({
                signUpLoading: false,
                sendKycLinkLoading: false,
                validateOTPLoading: false,
              })
            );
          }
        } else {
          dispatch(remittanceActions.setModalType("KYC_AGGREMENT"));
          dispatch(remittanceActions.resetRemitterDocumentsState());
          dispatch(
            remittanceActions.setSelectedRemitter({
              id: response?.data?.id,
              userKey: response?.data?.user_key,
            })
          );
          dispatch(
            remittanceActions.setAddRemitterResponse({
              signUpLoading: false,
              sendKycLinkLoading: false,
              validateOTPLoading: false,
            })
          );
          onRefreshClick();
        }
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        dispatch(
          remittanceActions.setAddRemitterResponse({
            signUpLoading: false,
            sendKycLinkLoading: false,
            error: errorMsg,
          })
        );
        toast.error(errorMsg);
        logApiErrorToSentry(err, "addRemitterValidateOtp remittanceApi.getAddRemitterValidateOtp");
      });
  };

  const columns = [
    {
      key: "fullName",
      label: "Remitter Name",
      render: (r: any) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || "—",
    },
    { key: "email", label: "Remitter Email" },
    { key: "phoneNumber", label: "Remitter Mobile" },
    {
      key: "kycStatus",
      label: "KYC Status",
      render: (r: any) => <KycStatus kycStatus={r?.kycStatus} />,
    },
    { key: "panNumber", label: "Remitter PAN" },
  ];

  const rawRemitters = remitterState?.remitter?.remitterList?.data?.content || [];
  const pan = calculator?.amountCalculation?.pan_number;
  const remitterList = pan
    ? rawRemitters.filter(
        (r: any) => r?.panNumber?.trim()?.toUpperCase() === pan.trim().toUpperCase()
      )
    : rawRemitters;
  const totalPages = remitterState?.remitter?.remitterList?.data?.totalPages ?? 1;
  const totalElements = remitterState?.remitter?.remitterList?.data?.totalElements ?? 0;
  const loading =
    remitterState?.remitter?.remitterList?.loading || remitterState?.remitter?.remitterList?.reload;

  const openAvanseQuoteModal = (remitterRow: any) => {
    const request = getSearchQuoteDetailsRequest(
      authState,
      remitterRow,
      calculator?.amountCalculation
    );
    if (request) {
      dispatch(remittanceActions.fetchSearchQuoteDetailsRequested(request));
    }
    dispatch(remittanceActions.setAvanseQuoteModalOpen(true));
  };

  const handleAvanseQuoteConfirm = () => {
    if (!remitter?.selectedQuoteUniqueKey) return;

    dispatch(remittanceActions.setAvanseQuoteModalOpen(false));
    dispatch(remittanceActions.setPendingAvanseQuoteFlow(false));
    dispatch(remittanceActions.setActiveStepNumber(3));
    dispatch(remittanceActions.resetRecipientState());
    navigate("/send/recipient");
  };

  const handleProceedWithoutQuote = () => {
    dispatch(remittanceActions.setAvanseQuoteModalOpen(false));
    dispatch(remittanceActions.setPendingAvanseQuoteFlow(false));
    dispatch(remittanceActions.setSelectedQuoteUniqueKey(null));
    dispatch(remittanceActions.setProceedWithoutQuote(false));
    dispatch(remittanceActions.setActiveStepNumber(3));
    dispatch(remittanceActions.resetRecipientState());
    navigate("/send/recipient");
  };

  const handleAvanseQuoteModalClose = () => {
    dispatch(remittanceActions.setAvanseQuoteModalOpen(false));
    dispatch(remittanceActions.setPendingAvanseQuoteFlow(false));
    dispatch(remittanceActions.setSelectedQuoteUniqueKey(null));
    dispatch(remittanceActions.setProceedWithoutQuote(false));
  };

  const handleKycSuccessProceed = () => {
    dispatch(remittanceActions.setModalType(""));

    const selectedRemitter = {
      ...remitter?.selectedRemitter,
      isDocVerified: true,
    };

    dispatch(remittanceActions.setSelectedRemitter(selectedRemitter));
    onRefreshClick();

    if (isAvanseMasterVendor(authState) && remitter?.pendingAvanseQuoteFlow) {
      if (isRemitterProfileIncomplete(selectedRemitter)) {
        navigate("/send/remitter/view");
        return;
      }

      openAvanseQuoteModal(selectedRemitter);
      return;
    }

    navigate("/send/remitter/view");
  };

  return (
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px]">
        {/* {loading && <Loader /> } */}
        <ToastContainer autoClose={3000} position="top-right" />

        {/* Nav Bar */}
        <SearchNavBar
          title="Remitter"
          value={searchedInput}
          onChange={setSearchedInput}
          onRefresh={onRefreshClick}
          addButtonTitle={AppStrings.SEARCH_NAV_BAR.REMITTER_ADD_BUTTON}
          onAddClick={() => {
            dispatch(remittanceActions.setModalType("ADD_REMITTER_MODAL"));
            remittanceActions.setAddRemitterInput({
              email: "",
              mobile: "",
              otp: "",
              isSentOtp: false,
            });
          }}
        />

        <DataTable
          data={remitterList}
          columns={columns}
          loading={loading}
          emptyState={(() => (
            <div className="flex flex-col items-center">
              <RemitterBanner width={100} height={100} />
              <p className="font-normal text-sm">
                {remitterState?.remitter?.remitterList?.error ||
                  AppStrings.REMITTER_SCREEN.EMPTY_MSG}
              </p>
            </div>
          ))()}
          detailRenderer={(row) => <RemitterDetails remitterData={row} />}
          pagination={{
            page: pagination.page,
            size: pagination.size,
            totalElements: totalElements,
            totalPages: totalPages,
            onPageChange: (p) => setPagination((prev) => ({ ...prev, page: p })),
            onSizeChange: (s) => {
              setPagination((prev) => ({ ...prev, size: s, page: 0 }));
            },
          }}
        />

        <AddRemitterModal
          visible={remitterState?.modalType === "ADD_REMITTER_MODAL"}
          onSubmit={(type) => addRemitter(type)}
          onClose={() => {
            dispatch(remittanceActions.setModalType(""));
          }}
        />

        <UserDocumentsModal
          visible={remitterState?.modalType === "USER_DOCUMENTS"}
          onClose={() => {
            dispatch(remittanceActions.setModalType(""));
            dispatch(remittanceActions.setPendingAvanseQuoteFlow(false));
            onRefreshClick();
          }}
        />

        <KycAggrementModal
          open={remitterState?.modalType === "KYC_AGGREMENT"}
          onYes={() => {
            dispatch(remittanceActions.setModalType("USER_DOCUMENTS"));
          }}
          onNo={() => {
            dispatch(remittanceActions.setModalType(""));
            dispatch(remittanceActions.setPendingAvanseQuoteFlow(false));
          }}
        />
        <KycSuccessModal
          open={remitterState?.modalType === "KYC_SUCCESS"}
          onYes={handleKycSuccessProceed}
          onNo={() => {
            dispatch(remittanceActions.setModalType(""));
            dispatch(remittanceActions.setPendingAvanseQuoteFlow(false));
            onRefreshClick();
          }}
        />

        <SelectQuoteModal
          open={remitter?.avanseQuoteModalOpen ?? false}
          onClose={handleAvanseQuoteModalClose}
          onConfirm={handleAvanseQuoteConfirm}
          onProceedWithout={handleProceedWithoutQuote}
        />
      </div>
    </>
  );
};

export default RemittanceRemitter;
